import { useCallback, useState } from "react";
import TextSlideView from "../lib/TextSlideView.tsx";
import {
  applyTemplate,
  autoUpdateNote,
  FIELD_LABELS,
  LAYOUT_FIELDS,
  TEMPLATES,
  type SlideLayout,
  type TextSlide,
} from "../lib/text-slide.ts";

export type { TextSlide };

// Built-in text-slide editor. The preview is the SAME component the TV runs
// (see TextSlideView), so what is approved here is what goes on the wall.
export default function TextSlideEditor({
  initial,
  onCancel,
  onSave,
}: {
  initial: TextSlide | null;
  onCancel: () => void;
  onSave: (slide: TextSlide) => Promise<void> | void;
}) {
  const [slide, setSlide] = useState<TextSlide>(
    initial ?? {
      name: "",
      h: "",
      s: "",
      layout: "event",
      bg: TEMPLATES[0].bg,
      fg: TEMPLATES[0].fg,
      accent: TEMPLATES[0].accent,
      eyebrow: TEMPLATES[0].seed?.eyebrow ?? "",
    },
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [fits, setFits] = useState(true);
  // Stable identity, or the effect in TextSlideView re-subscribes every render.
  const onFits = useCallback((f: boolean) => setFits(f), []);

  const set = (patch: Partial<TextSlide>) => setSlide((s) => ({ ...s, ...patch }));

  const layout: SlideLayout = slide.layout ?? "centered";
  const fields = LAYOUT_FIELDS[layout];
  const labels = FIELD_LABELS[layout];

  async function save() {
    if (!slide.name.trim() || !slide.h?.trim()) {
      setErr("A name and a headline are required.");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await onSave({ ...slide, name: slide.name.trim() });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
      setSaving(false);
    }
  }

  return (
    <div className="modal-scrim" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{initial ? "Edit text slide" : "New text slide"}</h2>

        {autoUpdateNote(slide) && (
          <div className="banner banner--warn" role="status">
            {autoUpdateNote(slide)}
          </div>
        )}

        <div className="slide-preview-frame">
          <TextSlideView slide={slide} fit="box" onFits={onFits} />
        </div>
        {!fits && (
          <div className="banner banner--warn" role="status">
            This is too long to fit — the screen will cut off the bottom. Shorten
            it, or move some of it to the subtext.
          </div>
        )}

        <div className="field">
          <span>Template</span>
          <div className="templates">
            {TEMPLATES.map((t) => {
              const on =
                layout === t.layout && slide.bg === t.bg && slide.fg === t.fg;
              return (
                <button
                  key={t.id}
                  type="button"
                  className={"template" + (on ? " template--on" : "")}
                  aria-pressed={on}
                  title={t.hint}
                  onClick={() => set(applyTemplate(slide, t))}
                >
                  <span
                    className="template-chip"
                    style={{ background: t.bg, color: t.fg }}
                    aria-hidden="true"
                  >
                    <span style={{ background: t.accent }} />
                    Aa
                  </span>
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <label className="field">
          <span>Name (internal)</span>
          <input
            value={slide.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="e.g. Harvest Festival"
          />
        </label>

        {/* Only the fields this layout actually renders — no typing into a box
            that will not appear on the screen. */}
        {fields.includes("eyebrow") && (
          <label className="field">
            <span>{labels.eyebrow?.label ?? "Small line above"}</span>
            <input
              value={slide.eyebrow ?? ""}
              onChange={(e) => set({ eyebrow: e.target.value })}
              placeholder={labels.eyebrow?.placeholder}
            />
          </label>
        )}
        <label className="field">
          <span>{labels.h?.label ?? "Headline"}</span>
          {layout === "scripture" ? (
            <textarea
              value={slide.h ?? ""}
              rows={3}
              onChange={(e) => set({ h: e.target.value })}
              placeholder={labels.h?.placeholder}
            />
          ) : (
            <input
              value={slide.h ?? ""}
              onChange={(e) => set({ h: e.target.value })}
              placeholder={labels.h?.placeholder}
            />
          )}
        </label>
        {fields.includes("m") && (
          <label className="field">
            <span>{labels.m?.label ?? "Details"}</span>
            <input
              value={slide.m ?? ""}
              onChange={(e) => set({ m: e.target.value })}
              placeholder={labels.m?.placeholder}
            />
          </label>
        )}
        {fields.includes("s") && (
          <label className="field">
            <span>{labels.s?.label ?? "Subtext (optional)"}</span>
            <textarea
              value={slide.s ?? ""}
              rows={2}
              onChange={(e) => set({ s: e.target.value })}
              placeholder={labels.s?.placeholder}
            />
          </label>
        )}

        <div className="field-row">
          <label className="field">
            <span>Background</span>
            <input
              type="color"
              value={slide.bg ?? "#000000"}
              onChange={(e) => set({ bg: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Text color</span>
            <input
              type="color"
              value={slide.fg ?? "#ffffff"}
              onChange={(e) => set({ fg: e.target.value })}
            />
          </label>
          <label className="field">
            <span>Accent</span>
            <input
              type="color"
              value={slide.accent ?? "#a8854d"}
              onChange={(e) => set({ accent: e.target.value })}
            />
          </label>
        </div>

        {err && <div className="banner banner--error">{err}</div>}

        <div className="modal-actions">
          <button className="btn btn--ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="btn" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save slide"}
          </button>
        </div>
      </div>
    </div>
  );
}
